import React, { useEffect, useState } from "react";
import { useIntl, FormattedMessage } from "react-intl";
import { useNavigate, useParams } from "react-router-dom";
import { Col, Row } from "reactstrap";
import ButtonComponent from "../../components/ButtonComponent";
import TableComponent, { OperationsCell } from "../../components/TableComponent";
import { ClientQuotationDTO } from "../../models/Quotation/ClientQuotationDTO";
import { ClientQuotationService } from "../../services/ClientQuotationService";
import { Utilities } from "../../helpers/Utilities";
import Toast from "../../helpers/Toast";
import { Parameter } from "../../models/Parameter";
import { AssistanceDTO } from "../../models/Assistance/AssistanceDTO";

type Props = {
  assistanceStatus: string;
  assistanceId: number;
  clientId?: number;
  reloadSignal: boolean;
  onReloadComplete: () => void;
};

export default function AssistanceClientQuotations({ ...props }: Props) {
  const intl = useIntl();
  const navigate = useNavigate();
  const service = new ClientQuotationService();

  const [data, setData] = useState<ClientQuotationDTO[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [pageCount, setPageCount] = useState(1);
  const pageSizes = [5, 15, 30];
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(5);
  const [currentPage, setCurrentPage] = useState<number>(1);

  // Colunas: similar às do ClientQuotationIndex, mas podes cortar as que não são relevantes
  const columns = React.useMemo(
    () => [
      {
        Header: "",
        id: "operations",
        width: 4,
        Cell: ({ row }: any) => {
          const quotation: ClientQuotationDTO = row.original;
          return (
            <div style={{ display: "flex", gap: "0.5rem" }}>

              {OperationsCell(quotation.id.toString(), "clientQuotation")}
            </div>
          );
        },
      },
      {
        Header: <FormattedMessage id="Screens.ClientQuotation.Table.QuotationNumber" />,
        accessor: "id",
        width: 15,
      },
      {
        Header: <FormattedMessage id="Screens.ClientQuotation.Table.Client" />,
        accessor: "client.name",
        width: 40,
      },
      {
        Header: <FormattedMessage id="Screens.ClientQuotation.Table.Status" />,
        accessor: "status.frontendName",
        width: 20,
      },
      {
        Header: <FormattedMessage id="Screens.ClientQuotation.Table.Assistance" />,
        accessor: "assistance",
        Cell: (props: { value: AssistanceDTO | null }) => {
          if (!props.value) return null;
          return (
            <span>
              {props.value.erpDocType} {props.value.erpSerie} {props.value.erpNumDoc}
            </span>
          );
        },
        width: 10,
      },
      {
        Header: <FormattedMessage id="Screens.ClientQuotation.Table.TotalAmount" />,
        accessor: "sellingPrice",
        width: 10,
      },
    ],
    []
  );

  const fetchData = async (page: number, pageSize: number) => {
    setIsLoading(true);
    var parameters = [new Parameter("assistance", props.assistanceId)];


    var response = await service.GetAll(
      parameters,
      [],
      page,
      pageSize
    );

    if (response.success !== true) {
      setData([]);
      setPageCount(0);
      setIsLoading(false);
      setPageSize(5);
      setCurrentPage(1);
      Toast.Show("error", response.message);
      return;
    }

    setData(response.items);
    setPageCount(response.totalPages);
    setIsLoading(false);
    setPageSize(pageSize);
    setCurrentPage(page);
  };


  useEffect(() => {
    fetchData(page, pageSize);
  }, [props.assistanceId, page, pageSize]);

  return (
    <>
      {props.assistanceStatus === "EM_CURSO" && (
        <Row style={{ margin: "1rem 0px" }}>
          <Col xs={4} sm={4} md={4} lg={4} xl={4}>
            <ButtonComponent
              text={intl.formatMessage({ id: "Screens.Assistances.Quotations.Create" })}
              onClick={() => navigate(`/clientQuotations/create?assistance=${props.assistanceId}&client=${props.clientId}`)}
              style={{
                minHeight: "37px",
                marginTop: "10px",
                marginBottom: "10px",
                display: "flex",
                justifyContent: "center",
              }}
            />
          </Col>
        </Row>
      )}
      <TableComponent
        data={data}
        columns={columns}
        manualPagination
        loading={isLoading}
        fetchData={fetchData}
        pageCount={pageCount}
        initialPage={page}
        initialPageSize={pageSize}
        pageSizes={pageSizes}
        goToPage={undefined}
        initialSortBy={[]}
      />
    </>
  );
}